import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  map,
  find,
  findIndex,
  cloneDeep,
  toLower,
  replace,
  noop,
  isEmpty,
  forEach,
  size,
  isNumber,
} from 'lodash';
import ThumbnailDownload from 'components/DocumentDownload/ThumbnailDownload';
import DropZoneFileManager from 'components/DropZone/DropZoneFileManager';
import CompanyLookup from 'components/CompanyLookup/CompanyLookup';
import { trackClick } from 'utility/analytics';
import { Formik } from 'formik';
import { Button } from 'theme/Button';
import { FormInput, Form, FormFooter } from 'theme/Form';
import { Message, Field } from '@zendeskgarden/react-forms';
import { Row, Col } from '@zendeskgarden/react-grid';
import Flex from 'styled-flex-component';
import { variables } from 'theme/variables';
import { getTags, addTag } from 'state/tags/actions';
import { getCategories, getContent } from 'state/content/actions';
import { FormWrapper } from 'theme/AuthForms.styles';
import { contentMetadataShape, pluginPropsShape } from 'propTypesObjects';
import { MD } from '@zendeskgarden/react-typography';
import history from 'historyContainer';
import Icon from 'components/Icon/Icon';
import { useBranch } from 'baobab-react/hooks';
import MetadataCategories from './MetadataCategories';
import MetadataMultiSelect from './MetadataMultiSelect';
import { MetaDataFormWrapper } from './MegadraftWrapper.styles';
import { Checkbox } from './plugins/tableview/FormComponents';

const { spacing } = variables;

const tagsDefault = [];
const metadataDefault = {};

function MetadataForm({
  category,
  tags = tagsDefault,
  saveContent,
  metadata = metadataDefault,
  setEditor,
  objectId,
  contentType,
  clientId,
  editor,
  pluginProps,
}) {
  const { category: pluginCategory, tags: pluginTags } = pluginProps || {};
  const metadataCopy = metadata ? cloneDeep(metadata) : {};
  const [heroImage, setHeroImage] = useState(metadataCopy?.image);
  const [showSetImage, setShowImage] = useState();
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [coAuthorOrCompanyVisible, setCoAuthorOrCompanyVisible] =
    useState(false);
  const [checked, setChecked] = useState(false);

  const { contentDetailsMap = {} } = useBranch({
    contentDetailsMap: ['content', 'details'],
  });
  const contentDetails = contentDetailsMap[objectId] || {};

  const isChecked = () => {
    setChecked(!checked);
  };

  function closeOverlay() {
    if (
      window.confirm(
        'Are you sure you want to close this window? Your changes will be lost.'
      )
    ) {
      if (metadata && editor) {
        setEditor(false);
      } else {
        history.push('/dashboard/content-editor');
      }
    }
  }

  function header() {
    return <div style={{ marginBottom: '10px' }} />;
  }

  useEffect(() => {
    setSelectedTags(pluginTags || tags);
  }, [availableTags.length, tags, pluginTags]);

  useEffect(() => {
    if (availableCategories.length && category) {
      setSelectedCategory(
        find(availableCategories, { id: pluginCategory || category })
      );
    }
  }, [
    availableCategories,
    availableCategories.length,
    category,
    pluginCategory,
  ]);

  const onSetTags = useCallback(async (_tags) => {
    const newTagIndex = findIndex(_tags, { add: true });
    if (newTagIndex > -1) {
      const addedTag = await addTag({ name: _tags[newTagIndex].label });
      if (addedTag?.id) {
        const clonedTags = cloneDeep(_tags);
        clonedTags[newTagIndex] = addedTag;
        setSelectedTags(clonedTags);
      } else {
        alert('There was an error adding tag.');
      }
    } else {
      setSelectedTags(_tags);
    }
  }, []);

  function handleSlug(values) {
    const valuesCopy = cloneDeep(values);
    if (valuesCopy.slug === '') {
      valuesCopy.slug = toLower(replace(valuesCopy.title, /\s+/g, '-'));
    }
    return valuesCopy;
  }

  const submitMetadata = useCallback(
    async (values, { setSubmitting, setStatus }) => {
      const valuesCopy = handleSlug(values);
      trackClick('submitMetadata', 'Submit', 'Content');
      setSubmitting(true);
      const finalValues = contentDetails?.result || {};
      // filters out empty values and only replaces the ones that have been edited
      forEach(valuesCopy, (val, key) => {
        if (!isEmpty(val)) {
          finalValues[key] = val;
        }
      });
      const contentSubmit = await saveContent({
        category: selectedCategory.id,
        tags: map(selectedTags, 'id'),
        metadata: { ...finalValues, image: heroImage },
      });
      const { error } = contentSubmit || {};
      if (error) {
        setStatus({ error });
      } else {
        setStatus({ success: true });
        if (objectId) {
          setTimeout(() => {
            getContent(objectId);
            setEditor(false);
          }, 800);
        } else {
          setShowImage(true);
        }
      }
      setSubmitting(false);
    },
    [
      heroImage,
      objectId,
      saveContent,
      selectedCategory.id,
      selectedTags,
      setEditor,
      contentDetails?.result,
    ]
  );

  const _onSaveRequest = useCallback((savedImages) => {
    setHeroImage(savedImages[savedImages.length - 1].saved.id);
  }, []);

  const toggleCoAuthorOrCompany = useCallback(
    (resetField) => {
      // Resets the co writer and company fields when toggling these fields "off"/"remove"
      if (coAuthorOrCompanyVisible) {
        resetField('co_writer', {});
        resetField('co_company', {});
      }
      // actually toggles these fields to visible or not
      setCoAuthorOrCompanyVisible(
        (coAuthorOrCompanyVisiblePrevious) => !coAuthorOrCompanyVisiblePrevious
      );
    },
    [coAuthorOrCompanyVisible]
  );

  // This just makes sure that once the metadata is loaded, if there is a co writer or company, it will show the fields
  useEffect(() => {
    if (
      isNumber(metadata?.co_writer?.value) ||
      isNumber(metadata?.co_company?.value)
    ) {
      setCoAuthorOrCompanyVisible(true);
    }
  }, [metadata?.co_company?.value, metadata?.co_writer?.value]);

  useEffect(() => {
    async function getTagsData() {
      const _tags = await getTags();
      setAvailableTags(_tags?.results || []);
    }
    getTagsData();

    async function getCategoriesData() {
      const _categories = await getCategories();
      setAvailableCategories(_categories?.results || []);
    }
    getCategoriesData();
  }, []);

  function renderOptions() {
    if (!objectId && !showSetImage) {
      return null;
    }
    if (heroImage) {
      return (
        <div>
          <ThumbnailDownload
            onRemoveFile={() => setHeroImage(undefined)}
            documentItem={{ fileId: heroImage }}
          />
        </div>
      );
    }
    return (
      <span>
        <DropZoneFileManager
          type="upload"
          uploadOnDrop
          multiple={false}
          isImage
          showUploadAndRender={false}
          objectId={objectId}
          contentType={contentType}
          disableDelete
          onSaved={(...img) => _onSaveRequest(...img)}
          renderHeader={() => header()}
        />
      </span>
    );
  }

  return (
    <MetaDataFormWrapper>
      <Formik
        initialValues={{
          title: metadataCopy?.title || '',
          slug: metadataCopy?.slug || '',
          writer: metadataCopy?.writer || '',
          description: metadataCopy?.description || '',
          company: metadataCopy?.company || '',
          co_writer: metadataCopy?.co_writer || '',
          co_company: metadataCopy?.co_company || '',
          button_name: metadataCopy?.button_name || '',
          button_url: metadataCopy?.button_url || '',
        }}
        validate={noop}
        onSubmit={submitMetadata}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          status,
        }) => (
          <Form size="large" onSubmit={handleSubmit}>
            <Row style={{ display: 'flex', justifyContent: 'end' }}>
              <Icon
                buttonStyle
                button
                onClick={() => closeOverlay()}
                icon="icon-close"
                fontSize="12px"
              />
            </Row>
            <header
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#0268ef',
                fontSize: '20px',
                fontWeight: 'bold',
                height: '30px',
              }}
            >
              Create Content
            </header>
            {!showSetImage ? (
              <span>
                <Row>
                  <Col md={12}>
                    <FormWrapper spacing="0px">
                      <FormInput
                        required
                        placeholder="Title"
                        type="title"
                        name="title"
                        autoComplete="current-title"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title || ''}
                        showMessage
                        error={errors.title && touched.title && errors.title}
                        inputMediaWrapperStyle={{
                          display: 'flex',
                        }}
                      />
                    </FormWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormWrapper spacing="20px">
                      <FormInput
                        required
                        placeholder="Slug (optional)"
                        type="slug"
                        name="slug"
                        autoComplete="current-slug"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.slug || ''}
                        showMessage
                        error={errors.slug && touched.slug && errors.slug}
                        inputMediaWrapperStyle={{
                          display: 'flex',
                        }}
                      />
                    </FormWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col size="12">
                    <FormWrapper spacing="20px">
                      <Field>
                        <CompanyLookup
                          searchKey="user"
                          validation={undefined}
                          selectedValue={values.writer?.label || ''}
                          medium
                          clientId={clientId}
                          title="Writer"
                          defaultValue="Writer"
                          setSelectedValue={(value) => {
                            setFieldValue('writer', value);
                          }}
                        />
                      </Field>
                    </FormWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col size="12">
                    <FormWrapper spacing="20px">
                      <Field>
                        <CompanyLookup
                          required
                          searchKey="company"
                          validation={undefined}
                          selectedValue={values.company?.label || ''}
                          medium
                          clientId={clientId}
                          title="Company"
                          defaultValue="Company"
                          setSelectedValue={(value) => {
                            setFieldValue('company', value);
                          }}
                        />
                      </Field>
                    </FormWrapper>
                  </Col>
                </Row>
                {coAuthorOrCompanyVisible ||
                !isEmpty(metadata?.co_writer?.value) ||
                !isEmpty(metadata?.co_company?.value) ? (
                  <div>
                    <Row>
                      <Col size="12">
                        <FormWrapper spacing="20px">
                          <Field>
                            <CompanyLookup
                              searchKey="user"
                              validation={undefined}
                              selectedValue={values?.co_writer?.label || ''}
                              medium
                              clientId={clientId}
                              title="Writer"
                              defaultValue="Co-Writer"
                              setSelectedValue={(value) => {
                                setFieldValue('co_writer', value);
                              }}
                            />
                          </Field>
                        </FormWrapper>
                      </Col>
                    </Row>
                    <Row>
                      <Col size="12">
                        <FormWrapper spacing="20px">
                          <Field>
                            <CompanyLookup
                              required
                              searchKey="company"
                              validation={undefined}
                              selectedValue={values?.co_company?.label || ''}
                              medium
                              clientId={clientId}
                              title="Company"
                              defaultValue="Co-Company"
                              setSelectedValue={(value) => {
                                setFieldValue('co_company', value);
                              }}
                            />
                          </Field>
                        </FormWrapper>
                      </Col>
                    </Row>
                  </div>
                ) : null}
                <Row>
                  <Col
                    size="12"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <FormWrapper spacing="20px">
                      <Button
                        small
                        ignoreLineHeight
                        link
                        blue
                        bold
                        fontAwesomeIconBeforePrefix="far"
                        onClick={() => toggleCoAuthorOrCompany(setFieldValue)}
                        fontAwesomeIconBefore
                        iconBefore={
                          coAuthorOrCompanyVisible ||
                          !isEmpty(metadata?.co_writer?.value) ||
                          !isEmpty(metadata?.co_company?.value)
                            ? 'minus-circle'
                            : 'plus-circle'
                        }
                      >
                        {coAuthorOrCompanyVisible ||
                        !isEmpty(metadata?.co_writer?.value) ||
                        !isEmpty(metadata?.co_company?.value)
                          ? 'Remove Co-Author and Company'
                          : 'Add A Co-Author and Company'}
                      </Button>
                    </FormWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormWrapper spacing="20px">
                      <FormInput
                        textarea
                        placeholder="Description"
                        type="description"
                        name="description"
                        autoComplete="current-description"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.description || ''}
                        showMessage
                        error={
                          errors.description &&
                          touched.description &&
                          errors.description
                        }
                        inputMediaWrapperStyle={{
                          display: 'flex',
                        }}
                      />
                    </FormWrapper>
                  </Col>
                </Row>
              </span>
            ) : null}
            {availableCategories && size(availableCategories) ? (
              <Row>
                <Col md={12}>
                  <MetadataCategories
                    selectedItem={selectedCategory}
                    setSelectedItem={setSelectedCategory}
                    options={availableCategories}
                  />
                </Col>
              </Row>
            ) : null}
            {availableTags && size(availableTags) ? (
              <Row>
                <Col md={12}>
                  <MetadataMultiSelect
                    selectedItems={selectedTags}
                    setSelectedItems={onSetTags}
                    options={availableTags}
                  />
                </Col>
              </Row>
            ) : null}
            <Row>
              <Col>{renderOptions()}</Col>
            </Row>
            {isEmpty(contentDetails?.result?.button_name) ||
            isEmpty(contentDetails?.result?.button_url) ? (
              <Row>
                <Col>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '15px',
                    }}
                  >
                    <Checkbox value={checked} onChange={isChecked} />
                    <MD
                      bold
                      style={{
                        marginLeft: '10px',
                      }}
                    >
                      Add a Call-to-Action
                    </MD>
                  </div>
                </Col>
              </Row>
            ) : null}
            {checked ||
            !isEmpty(contentDetails?.result?.button_name) ||
            !isEmpty(contentDetails?.result?.button_url) ? (
              <div>
                <Row>
                  <Col>
                    <FormWrapper spacing="20px">
                      <FormInput
                        placeholder={metadataCopy.button_name || 'Button Name'}
                        type="button_name"
                        name="button_name"
                        autoComplete="current-button_name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={
                          values.button_name ||
                          contentDetails?.result?.button_name ||
                          ''
                        }
                        showMessage
                        error={
                          errors.button_name &&
                          touched.button_name &&
                          errors.button_name
                        }
                        inputMediaWrapperStyle={{
                          display: 'flex',
                        }}
                      />
                    </FormWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormWrapper spacing="20px">
                      <FormInput
                        placeholder="Button URL"
                        type="button_url"
                        name="button_url"
                        autoComplete="current-button_url"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={
                          values.button_url ||
                          contentDetails?.result?.button_url ||
                          ''
                        }
                        showMessage
                        error={
                          errors.button_url &&
                          touched.button_url &&
                          errors.button_url
                        }
                        inputMediaWrapperStyle={{
                          display: 'flex',
                        }}
                      />
                    </FormWrapper>
                  </Col>
                </Row>
              </div>
            ) : null}
            <Row>
              <Col md={12}>
                {status && status.error ? (
                  <FormFooter>
                    <Field>
                      <Message validation="error">{status.error}</Message>
                    </Field>
                  </FormFooter>
                ) : null}
                <Flex justifyCenter>
                  <Button
                    error={status && status.error}
                    buttonStatus
                    loading={isSubmitting}
                    type="submit"
                    primary
                    style={{ width: '100%', marginTop: spacing }}
                  >
                    Save
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </MetaDataFormWrapper>
  );
}

MetadataForm.propTypes = {
  category: PropTypes.number,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
  saveContent: PropTypes.func,
  metadata: contentMetadataShape,
  setEditor: PropTypes.func,
  objectId: PropTypes.number,
  contentType: PropTypes.number,
  clientId: PropTypes.number,
  editor: PropTypes.bool,
  pluginProps: pluginPropsShape,
};

export default MetadataForm;
